/**
 * Centralized route helpers for generic DocType navigation.
 *
 * Rules of Engagement:
 * - Never hardcode /inventory/... or other paths in feature components
 * - Always use buildListRoute(doctype), buildDetailRoute(doctype, name), buildNewRoute(doctype)
 * - For connections, use buildConnectionRoute() - it owns URL+filters construction
 * - If you need a new route, add it to routes.ts first, then add to doctypeToRouteKey below
 * - Connection routing may throw in dev; list/detail/new have generic fallbacks
 *
 * NOTE:
 * We keep these helpers in a dedicated module to avoid hardcoding paths
 * in feature components and to keep all URL shapes governed in one place.
 */
import { routes } from "./routes";
import {
  getConnectionConfig,
  doctypeToConnectionKey,
  type ConnectionFilterContext,
} from "./docConnections";
import { logger } from "@/lib/utils/logger";

/**
 * DocType → route key mapping
 * 
 * This is the single source of truth for mapping DocType names to route keys.
 * Add new doctypes here when you add routes to routes.ts.
 */
const doctypeToRouteKey: Record<string, keyof typeof routes> = {
  "Customer": "customer",
  "Item": "item",
  "Item Price": "itemPrice",
  "Pricing Rule": "pricingRule",
  "Sales Order": "salesOrder",
  "Sales Invoice": "salesInvoice",
  "Payment Entry": "paymentEntry",
  "Stock Entry": "stockEntry",
  "Stock Reconciliation": "stockReconciliation",
  "Storefront Theme": "storefrontTheme",
  "Storefront Page Config": "storefrontPageConfig",
  "Item Customization Template": "customizationTemplate",
  "Shipping Rate Rule": "shippingRateRule",
};

function normalizeDoctype(doctype: string): string {
  return doctype.trim();
}

/**
 * Decode and normalize a doctype segment from a URL param.
 * Centralizes decoding/trim so callers don't double-normalize.
 */
export function decodeAndNormalizeDoctype(encodedDoctype?: string): string {
  if (!encodedDoctype) return "";
  try {
    return normalizeDoctype(decodeURIComponent(encodedDoctype));
  } catch {
    // If the string is malformed or already-decoded, fall back to raw + trim.
    return normalizeDoctype(encodedDoctype);
  }
}

/**
 * True if doctype is mapped to a canonical (custom) route.
 *
 * Use this to prefer custom routes over generic `/doc/:doctype/...` routes.
 */
export function isMappedDoctype(doctype: string): boolean {
  return Boolean(doctypeToRouteKey[normalizeDoctype(doctype)]);
}


/** One doctype's pretty routes (read-only view of `routes` + `doctypeToRouteKey`). */
export interface DoctypeRouteEntry {
  doctype: string;
  list: string;
  new: string;
  /** react-router pattern of the detail route, e.g. `/inventory/item/:name` */
  detailPattern: string;
}

const NAME_SENTINEL = "__name__";

/**
 * Read-only accessor for the doctype <-> pretty-route map, so URL -> doctype
 * resolution (plan 1968 page context) reuses this single source of truth.
 */
export function getDoctypeRouteTable(): ReadonlyArray<DoctypeRouteEntry> {
  return Object.entries(doctypeToRouteKey).map(([doctype, key]) => {
    const route = routes[key] as unknown as {
      list: string;
      new: string;
      detail: (name: string) => string;
    };
    return {
      doctype,
      list: route.list,
      new: route.new,
      detailPattern: route.detail(NAME_SENTINEL).replace(NAME_SENTINEL, ":name"),
    };
  });
}

/**
 * Build list route for a doctype
 * 
 * @param doctype - DocType name (e.g., "Item", "Item Price")
 * @returns URL string for list page
 * 
 * Never throws - returns generic fallback route if doctype is not mapped.
 */
export function buildListRoute(doctype: string): string {
  const normalizedDoctype = normalizeDoctype(doctype);
  const routeKey = doctypeToRouteKey[normalizedDoctype];
  if (routeKey) {
    const route = routes[routeKey];
    if (route && typeof (route as any).list === "string") {
      return (route as any).list as string;
    }
  }
  // Generic fallback route - works for any doctype
  return `/list/${encodeURIComponent(normalizedDoctype)}`;
}

/**
 * Build detail route for a doctype and document name
 * 
 * @param doctype - DocType name (e.g., "Item", "Item Price")
 * @param name - Document name/identifier
 * @returns URL string for detail page
 * 
 * Never throws - returns generic detail route if doctype is not mapped.
 */
export function buildDetailRoute(doctype: string, name: string): string {
  const normalizedDoctype = normalizeDoctype(doctype);
  const routeKey = doctypeToRouteKey[normalizedDoctype];
  if (routeKey) {
    const route = routes[routeKey];
    const detail = route ? (route as any).detail : undefined;
    if (typeof detail === "function") {
      return detail(name) as string;
    }
  }

  const doctypeEncoded = encodeURIComponent(normalizedDoctype);
  const nameEncoded = encodeURIComponent(name);

  // Disambiguation: allow opening docs literally named "new"
  if (name === "new") {
    return `/doc/${doctypeEncoded}/name/${nameEncoded}`;
  }

  // Generic fallback route - works for any doctype
  return `/doc/${doctypeEncoded}/${nameEncoded}`;
}

/**
 * Build new document route for a doctype
 * 
 * @param doctype - DocType name (e.g., "Item", "Item Price")
 * @param linkParams - Optional params to pre-fill link fields
 * @returns URL string for new document page
 * 
 * Never throws - returns generic create route if doctype is not mapped.
 */
export function buildNewRoute(
  doctype: string,
  linkParams?: {
    link_doctype: string;
    link_name: string;
  }
): string {
  const normalizedDoctype = normalizeDoctype(doctype);
  const key = doctypeToRouteKey[normalizedDoctype];
  
  // Build base route (mapped or generic)
  let baseRoute: string;
  if (key) {
    const route = routes[key];
    baseRoute = route && typeof (route as any).new === "string" ? ((route as any).new as string) : "";
  } else {
    // Generic fallback route - works for any doctype
    baseRoute = `/doc/${encodeURIComponent(normalizedDoctype)}/new`;
  }
  if (baseRoute.length === 0) {
    baseRoute = `/doc/${encodeURIComponent(normalizedDoctype)}/new`;
  }
  
  // Add link params if provided
  if (linkParams) {
    const params = new URLSearchParams({
      link_doctype: linkParams.link_doctype,
      link_name: linkParams.link_name,
    });
    return `${baseRoute}?${params.toString()}`;
  }

  return baseRoute;
}

/**
 * Legacy alias for buildNewRoute (for backward compatibility)
 * @deprecated Use buildNewRoute instead
 */
export function buildNewDocRoute(
  doctype: string,
  linkParams?: {
    link_doctype: string;
    link_name: string;
  }
): string {
  return buildNewRoute(doctype, linkParams);
}

/**
 * Legacy alias for buildDetailRoute (for backward compatibility)
 * @deprecated Use buildDetailRoute instead
 */
export function buildDocRoute(doctype: string, name: string): string {
  return buildDetailRoute(doctype, name);
}

/**
 * Build connection route with filters
 * 
 * This function owns the complete URL+filters construction for connections.
 * Components should never build filter URLs manually - always use this function.
 * 
 * @param sourceDoctype - Source doctype (e.g., "Item")
 * @param connectionKey - Connection key (e.g., "item_price", "pricing_rule")
 *   If not provided, will be derived from targetDoctype
 * @param ctx - Connection filter context
 * @param targetDoctype - Target doctype (optional, used if connectionKey not provided)
 * @returns Complete URL with filters query parameter
 */
export function buildConnectionRoute(
  sourceDoctype: string,
  connectionKeyOrTargetDoctype: string,
  ctx: ConnectionFilterContext,
  options?: {
    /**
     * If connectionKeyOrTargetDoctype is a target doctype, set this to true
     * to auto-derive the connection key
     */
    isTargetDoctype?: boolean;
  }
): string {
  // Determine connection key
  let connectionKey: string;
  if (options?.isTargetDoctype) {
    connectionKey = doctypeToConnectionKey(connectionKeyOrTargetDoctype);
  } else {
    connectionKey = connectionKeyOrTargetDoctype;
  }

  // Look up connection config
  const connection = getConnectionConfig(sourceDoctype, connectionKey);
  
  if (!connection) {
    const msg = `[buildConnectionRoute] No connection config for ${sourceDoctype}.${connectionKey}. Add it to docConnections.ts`;
    if (process.env.NODE_ENV === "development") {
      throw new Error(msg);
    }
    logger.error(msg);
    // In production, fallback to list route without filters
    const fallbackTarget = options?.isTargetDoctype 
      ? connectionKeyOrTargetDoctype 
      : sourceDoctype;
    return buildListRoute(fallbackTarget);
  }
  
  // Build base route
  const base = buildListRoute(connection.targetDoctype);
  
  // Build filters using connection config
  const filters = connection.buildFilters(ctx);
  
  // Encode filters into query string
  const qs = encodeURIComponent(JSON.stringify(filters));
  
  return `${base}?filters=${qs}`;
}

/**
 * Generate a short storage key for filters
 */
function generateFiltersKey(): string {
  // Prefer crypto-grade unique IDs to avoid collisions.
  try {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (uuid) {
      return `filters_${uuid}`;
    }
  } catch {
    // ignore
  }

  try {
    const bytes = new Uint8Array(16);
    globalThis.crypto?.getRandomValues?.(bytes);
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    if (hex) {
      return `filters_${Date.now()}_${hex}`;
    }
  } catch {
    // ignore
  }

  // Last resort (very old runtimes): time-only key.
  return `filters_${Date.now()}`;
}

/**
 * Build list route with filters (with URL length safety via token endpoint or storage fallback)
 * 
 * @param doctype - Target doctype (e.g., "Sales Order")
 * @param filters - Frappe filter array (3-tuple or 4-tuple)
 * @returns URL string with filters query parameter, token, or storage key
 */
/**
 * Build list route with filters (with URL length safety via token endpoint or storage fallback)
 * 
 * Never throws - always returns a valid route, falling back to unfiltered list if needed.
 * 
 * @param doctype - Target doctype (e.g., "Sales Order")
 * @param filters - Frappe filter array (3-tuple or 4-tuple)
 * @returns URL string with filters query parameter, token, or storage key
 */
export async function buildListRouteWithFilters(
  doctype: string,
  filters: Array<[string, string, any] | [string, string, string, any]>
): Promise<string> {
  // buildListRoute never throws, so this is safe
  const baseRoute = buildListRoute(doctype);
  
  if (!filters || filters.length === 0) {
    return baseRoute;
  }
  
  // Encode filters as JSON in query parameter
  const filtersJson = JSON.stringify(filters);
  const encoded = encodeURIComponent(filtersJson);
  
  // URL length safety: use token pattern if exceeds threshold
  const MAX_SAFE_URL_LENGTH = 1500;
  if (encoded.length > MAX_SAFE_URL_LENGTH) {
    // Try token endpoint first
    try {
      const { frappeMethod } = await import("@/lib/erp/utils");
      const tokenRes = await frappeMethod<{ token: string }>(
        "zaviago_backend.api.relationship_graph.get_list_route_token",
        {
          doctype,
          filters,
        }
      );
      // CRITICAL: Include doctype explicitly in URL
      const doctypeEncoded = encodeURIComponent(doctype);
      const rawToken = tokenRes?.token;
      if (typeof rawToken !== "string" || rawToken.length === 0) {
        // If backend doesn't return a token (contract mismatch), fall back to storage.
        throw new Error("Token endpoint returned empty token");
      }
      const token = encodeURIComponent(rawToken);
      return `${baseRoute}?filters_token=${token}&doctype=${doctypeEncoded}`;
    } catch (error) {
      logger.warn("[buildListRouteWithFilters] Token endpoint failed, using storage fallback:", error);
      
      // CRITICAL: Deterministic fallback - use sessionStorage
      const storageKey = generateFiltersKey();
      try {
        sessionStorage.setItem(`filters:${storageKey}`, filtersJson);
        // Store doctype with key for retrieval (explicit, not heuristic)
        sessionStorage.setItem(`filters_doctype:${storageKey}`, doctype);
        // Store timestamp for TTL cleanup
        sessionStorage.setItem(`filters_timestamp:${storageKey}`, Date.now().toString());
        // CRITICAL: Include doctype explicitly in URL
        const doctypeEncoded = encodeURIComponent(doctype);
        return `${baseRoute}?filters_key=${storageKey}&doctype=${doctypeEncoded}`;
      } catch (storageError) {
        // If storage fails (private mode, quota exceeded), log and return unfiltered
        logger.error("[buildListRouteWithFilters] Storage fallback failed:", storageError);
        return baseRoute;  // Return unfiltered route (better than broken URL)
      }
    }
  }
  
  // Short filters: include directly in URL (with doctype for consistency)
  const doctypeEncoded = encodeURIComponent(doctype);
  return `${baseRoute}?filters=${encoded}&doctype=${doctypeEncoded}`;
}
